// src/components/workspace/costing/PricingWizard.tsx
// AI-Guided Pricing Wizard - 4 steps to configure pricing profile

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Calculator, Package, CheckCircle, ArrowLeft, ArrowRight,
  Sparkles, Loader2, Plus, Trash2, Info, DollarSign, Percent,
  Save, X, AlertCircle, TrendingUp, Zap
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

// Types
interface LaborCategory {
  id: string;
  title: string;
  baseRate: number;
  gsaRate?: number;
  description?: string;
  yearsExperience?: string;
}

interface IndirectRates {
  fringe: number;
  overhead: number;
  gAndA: number;
  profit: number;
}

interface ODCCategory {
  id: string;
  name: string;
  description: string;
  estimatedAnnualCost: number;
}

interface WizardState {
  laborCategories: LaborCategory[];
  indirectRates: IndirectRates;
  odcCategories: ODCCategory[];
}

const STEPS = [
  { id: 'labor', title: 'Labor Categories', icon: Users, description: 'Define your labor categories and rates' },
  { id: 'indirect', title: 'Indirect Rates', icon: Calculator, description: 'Configure Fringe, Overhead, G&A, and Profit' },
  { id: 'odc', title: 'ODCs', icon: Package, description: 'Add Other Direct Costs categories' },
  { id: 'review', title: 'Review', icon: CheckCircle, description: 'Review and save your pricing profile' }
];

// AI Suggestions for labor categories - Base rates are SCA-aligned direct labor costs
// GSA rates are typical fully-loaded schedule rates for comparison
// These are BASE rates (before wrap rate) - lower than GSA which includes all burdens
const AI_SUGGESTED_LABOR = [
  { title: 'Program Manager', baseRate: 85, gsaRate: 165, yearsExperience: '10+', scaCode: '01020' },
  { title: 'Project Manager', baseRate: 72, gsaRate: 140, yearsExperience: '7+', scaCode: '01021' },
  { title: 'Senior Software Engineer', baseRate: 75, gsaRate: 145, yearsExperience: '8+', scaCode: '14074' },
  { title: 'Software Engineer', baseRate: 55, gsaRate: 105, yearsExperience: '4+', scaCode: '14073' },
  { title: 'Junior Software Engineer', baseRate: 40, gsaRate: 75, yearsExperience: '0-3', scaCode: '14071' },
  { title: 'Systems Administrator', baseRate: 48, gsaRate: 90, yearsExperience: '4+', scaCode: '14041' },
  { title: 'Cybersecurity Analyst', baseRate: 62, gsaRate: 120, yearsExperience: '5+', scaCode: '14042' },
  { title: 'Technical Writer', baseRate: 42, gsaRate: 80, yearsExperience: '3+', scaCode: '01313' },
  { title: 'Business Analyst', baseRate: 52, gsaRate: 100, yearsExperience: '5+', scaCode: '01111' },
  { title: 'Quality Assurance Specialist', baseRate: 45, gsaRate: 85, yearsExperience: '4+', scaCode: '13070' },
  { title: 'Help Desk Technician', baseRate: 32, gsaRate: 60, yearsExperience: '2+', scaCode: '14043' },
  { title: 'Administrative Support', baseRate: 28, gsaRate: 52, yearsExperience: '2+', scaCode: '01011' }
];

// Industry benchmark rates - Adjusted for competitive federal pricing
// Note: Lower rates = more competitive pricing
const INDUSTRY_BENCHMARKS = {
  fringe: { min: 28, max: 42, typical: 32, description: 'Benefits (health, 401k), FICA, PTO, insurance' },
  overhead: { min: 25, max: 80, typical: 45, description: 'Facilities, IT, indirect labor, equipment' },
  gAndA: { min: 8, max: 20, typical: 12, description: 'Executive, finance, HR, BD costs' },
  profit: { min: 6, max: 12, typical: 10, description: 'Fee/profit margin (FAR 15.404-4)' }
};

export function PricingWizard() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  const [wizardState, setWizardState] = useState<WizardState>({
    laborCategories: [],
    indirectRates: {
      fringe: 32,
      overhead: 45,  // Reduced from 75% for more competitive pricing
      gAndA: 12,     // Reduced from 15% for more competitive pricing
      profit: 10
    },
    odcCategories: []
  });

  useEffect(() => {
    if (profile) {
      loadExistingData();
    }
  }, [profile]);

  const loadExistingData = async () => {
    try {
      setLoading(true);

      // Load existing labor categories
      const { data: pricingData } = await supabase
        .from('enhanced_pricing_data')
        .select('pricing_components(*)')
        .eq('profile_id', profile?.id);

      const laborCategories: LaborCategory[] = [];
      const odcCategories: ODCCategory[] = [];

      pricingData?.forEach(entry => {
        entry.pricing_components?.forEach((comp: any) => {
          if (comp.category === 'Labor') {
            laborCategories.push({
              id: comp.id,
              title: comp.name,
              baseRate: comp.unit_cost,
              description: comp.description
            });
          } else if (comp.category !== 'Overhead') {
            odcCategories.push({
              id: comp.id,
              name: comp.name,
              description: comp.description || '',
              estimatedAnnualCost: comp.unit_cost * (comp.quantity || 1)
            });
          }
        });
      });

      // Load existing indirect rates
      const { data: indirectData } = await supabase
        .from('company_indirect_rates')
        .select('*')
        .eq('profile_id', profile?.id)
        .single();

      setWizardState(prev => ({
        ...prev,
        laborCategories,
        odcCategories,
        indirectRates: indirectData ? {
          fringe: indirectData.fringe_rate || 32,
          overhead: indirectData.overhead_rate || 75,
          gAndA: indirectData.ga_rate || 15,
          profit: indirectData.profit_rate || 10
        } : prev.indirectRates
      }));

    } catch (error) {
      console.error('Error loading existing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateWrapRate = () => {
    const { fringe, overhead, gAndA, profit } = wizardState.indirectRates;
    // Wrap rate calculation: Base * (1 + Fringe%) * (1 + OH%) * (1 + G&A%) * (1 + Profit%)
    return (1 + fringe / 100) * (1 + overhead / 100) * (1 + gAndA / 100) * (1 + profit / 100);
  };

  const addLaborCategory = () => {
    setWizardState(prev => ({
      ...prev,
      laborCategories: [
        ...prev.laborCategories,
        {
          id: `new-${Date.now()}`,
          title: '',
          baseRate: 100
        }
      ]
    }));
  };

  const updateLaborCategory = (id: string, updates: Partial<LaborCategory>) => {
    setWizardState(prev => ({
      ...prev,
      laborCategories: prev.laborCategories.map(cat =>
        cat.id === id ? { ...cat, ...updates } : cat
      )
    }));
  };

  const removeLaborCategory = (id: string) => {
    setWizardState(prev => ({
      ...prev,
      laborCategories: prev.laborCategories.filter(cat => cat.id !== id)
    }));
  };

  const addSuggestedLabor = (suggestion: typeof AI_SUGGESTED_LABOR[0]) => {
    const exists = wizardState.laborCategories.some(
      cat => cat.title.toLowerCase() === suggestion.title.toLowerCase()
    );
    if (!exists) {
      setWizardState(prev => ({
        ...prev,
        laborCategories: [
          ...prev.laborCategories,
          {
            id: `new-${Date.now()}`,
            ...suggestion
          }
        ]
      }));
    }
  };

  const addODCCategory = () => {
    setWizardState(prev => ({
      ...prev,
      odcCategories: [
        ...prev.odcCategories,
        {
          id: `new-${Date.now()}`,
          name: '',
          description: '',
          estimatedAnnualCost: 0
        }
      ]
    }));
  };

  const updateODCCategory = (id: string, updates: Partial<ODCCategory>) => {
    setWizardState(prev => ({
      ...prev,
      odcCategories: prev.odcCategories.map(cat =>
        cat.id === id ? { ...cat, ...updates } : cat
      )
    }));
  };

  const removeODCCategory = (id: string) => {
    setWizardState(prev => ({
      ...prev,
      odcCategories: prev.odcCategories.filter(cat => cat.id !== id)
    }));
  };

  const updateIndirectRate = (key: keyof IndirectRates, value: number) => {
    setWizardState(prev => ({
      ...prev,
      indirectRates: { ...prev.indirectRates, [key]: value }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Save indirect rates to company_indirect_rates table
      const { error: ratesError } = await supabase
        .from('company_indirect_rates')
        .upsert({
          profile_id: profile?.id,
          fringe_rate: wizardState.indirectRates.fringe,
          overhead_rate: wizardState.indirectRates.overhead,
          ga_rate: wizardState.indirectRates.gAndA,
          profit_rate: wizardState.indirectRates.profit,
          updated_at: new Date().toISOString()
        }, { onConflict: 'profile_id' });

      if (ratesError) throw ratesError;

      // Save labor categories to company_labor_categories table
      if (wizardState.laborCategories.length > 0) {
        // Delete existing labor categories for this profile
        await supabase
          .from('company_labor_categories')
          .delete()
          .eq('profile_id', profile?.id);

        // Insert new labor categories
        const laborToInsert = wizardState.laborCategories.map((cat, index) => ({
          profile_id: profile?.id,
          title: cat.title,
          description: cat.description || `${cat.yearsExperience || ''} years experience`,
          base_hourly_rate: cat.baseRate,
          gsa_rate: cat.gsaRate || null,
          years_experience_min: parseInt(cat.yearsExperience?.replace('+', '') || '0') || 0,
          sort_order: index,
          is_active: true
        }));

        const { error: laborError } = await supabase
          .from('company_labor_categories')
          .insert(laborToInsert);

        if (laborError) {
          console.error('Labor categories save error:', laborError);
          // Continue even if this fails - may not have migration applied yet
        }
      }

      // Save ODC categories to company_odc_categories table
      if (wizardState.odcCategories.length > 0) {
        // Delete existing ODC categories for this profile
        await supabase
          .from('company_odc_categories')
          .delete()
          .eq('profile_id', profile?.id);

        // Insert new ODC categories
        const odcToInsert = wizardState.odcCategories.map(cat => ({
          profile_id: profile?.id,
          name: cat.name,
          description: cat.description,
          category_type: 'other',
          estimated_unit_cost: cat.estimatedAnnualCost,
          unit: 'annual',
          estimated_annual_quantity: 1,
          is_recurring: true,
          is_active: true
        }));

        const { error: odcError } = await supabase
          .from('company_odc_categories')
          .insert(odcToInsert);

        if (odcError) {
          console.error('ODC categories save error:', odcError);
          // Continue even if this fails - may not have migration applied yet
        }
      }

      // Also save to legacy enhanced_pricing_data for backward compatibility
      let pricingDataId: string;
      const { data: existingPricing } = await supabase
        .from('enhanced_pricing_data')
        .select('id')
        .eq('profile_id', profile?.id)
        .limit(1)
        .single();

      if (existingPricing) {
        pricingDataId = existingPricing.id;
      } else {
        const { data: newPricing, error: createError } = await supabase
          .from('enhanced_pricing_data')
          .insert({
            profile_id: profile?.id,
            sector: 'General',
            naics_codes: ['000000'],
            notes: ''
          })
          .select('id')
          .single();

        if (createError) throw createError;
        pricingDataId = newPricing.id;
      }

      // Delete existing components
      await supabase
        .from('pricing_components')
        .delete()
        .eq('pricing_data_id', pricingDataId);

      // Insert labor categories as components
      const laborComponents = wizardState.laborCategories.map(cat => ({
        pricing_data_id: pricingDataId,
        category: 'Labor',
        name: cat.title,
        description: cat.description || `${cat.yearsExperience || ''} years experience`,
        unit_cost: cat.baseRate,
        quantity: 1,
        unit: 'hour',
        notes: cat.gsaRate ? `GSA Rate: $${cat.gsaRate}` : ''
      }));

      // Insert ODC categories as components
      const odcComponents = wizardState.odcCategories.map(cat => ({
        pricing_data_id: pricingDataId,
        category: 'Materials',
        name: cat.name,
        description: cat.description,
        unit_cost: cat.estimatedAnnualCost,
        quantity: 1,
        unit: 'annual',
        notes: ''
      }));

      if ([...laborComponents, ...odcComponents].length > 0) {
        const { error: componentsError } = await supabase
          .from('pricing_components')
          .insert([...laborComponents, ...odcComponents]);

        if (componentsError) throw componentsError;
      }

      toast.success('Pricing profile saved successfully!');
      navigate('/workspace/costing');
    } catch (error) {
      console.error('Error saving pricing profile:', error);
      toast.error('Failed to save pricing profile');
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderLaborStep();
      case 1:
        return renderIndirectRatesStep();
      case 2:
        return renderODCStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderLaborStep = () => (
    <div className="space-y-6">
      {/* AI Suggestions Panel */}
      <div className="bg-bw-surface-subtle rounded-lg p-4 border border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Sparkles className="h-5 w-5 text-bw-primary mt-0.5 mr-2" />
            <div>
              <h4 className="font-medium text-bw-ink">AI-Suggested Labor Categories</h4>
              <p className="text-sm text-bw-muted mt-1">
                Based on federal IT services contracts. Click to add to your profile.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {AI_SUGGESTED_LABOR.map((suggestion, index) => {
            const isAdded = wizardState.laborCategories.some(
              cat => cat.title.toLowerCase() === suggestion.title.toLowerCase()
            );
            return (
              <button
                key={index}
                onClick={() => !isAdded && addSuggestedLabor(suggestion)}
                disabled={isAdded}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center transition-colors ${
                  isAdded
                    ? 'bg-success-bg text-success cursor-default'
                    : 'bg-bw-surface border border-blue-200 text-bw-primary hover:bg-bw-primary-tint'
                }`}
              >
                {isAdded ? (
                  <CheckCircle className="h-3 w-3 mr-1" />
                ) : (
                  <Plus className="h-3 w-3 mr-1" />
                )}
                {suggestion.title}
                <span className="ml-1 text-xs text-bw-muted">${suggestion.baseRate}/hr</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Labor Categories List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-bw-ink">Your Labor Categories</h4>
          <button
            onClick={addLaborCategory}
            className="flex items-center px-3 py-1.5 text-sm text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom
          </button>
        </div>

        {wizardState.laborCategories.length === 0 ? (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-bw-muted">No labor categories yet</p>
            <p className="text-xs text-bw-muted mt-1">
              Click the suggestions above or add a custom category
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {wizardState.laborCategories.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center gap-4 p-3 bg-bw-surface border border-bw-border rounded-lg"
              >
                <div className="flex-1">
                  <input
                    type="text"
                    value={cat.title}
                    onChange={(e) => updateLaborCategory(cat.id, { title: e.target.value })}
                    placeholder="Job Title"
                    className="w-full text-sm font-medium border-none focus:ring-0 p-0"
                  />
                </div>
                <div className="w-32">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 text-bw-muted" />
                    <input
                      type="number"
                      value={cat.baseRate}
                      onChange={(e) => updateLaborCategory(cat.id, { baseRate: parseFloat(e.target.value) || 0 })}
                      className="w-20 text-sm border-none focus:ring-0 p-0"
                    />
                    <span className="text-xs text-bw-muted">/hr</span>
                  </div>
                </div>
                {cat.gsaRate && (
                  <div className="text-xs text-success bg-success-bg px-2 py-1 rounded">
                    GSA: ${cat.gsaRate}
                  </div>
                )}
                <button
                  onClick={() => removeLaborCategory(cat.id)}
                  className="text-bw-muted hover:text-urgent transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderIndirectRatesStep = () => {
    const wrapRate = calculateWrapRate();

    return (
      <div className="space-y-6">
        {/* Wrap Rate Preview */}
        <div className="bg-bw-surface-subtle rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-bw-ink">Your Wrap Rate</h4>
              <p className="text-sm text-bw-muted">
                $100 base rate = <span className="font-bold text-success">{formatCurrency(100 * wrapRate)}</span> fully loaded
              </p>
            </div>
            <div className="text-3xl font-bold text-success">{wrapRate.toFixed(2)}x</div>
          </div>

          {/* Visual Breakdown */}
          <div className="mt-4 flex items-center h-8 rounded-lg overflow-hidden">
            <div className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs"
                 style={{ width: `${100 / wrapRate}%` }}>
              Base
            </div>
            <div className="h-full bg-success-bg0 flex items-center justify-center text-white text-xs"
                 style={{ width: `${(wizardState.indirectRates.fringe / 100) * (100 / wrapRate)}%` }}>
              +{wizardState.indirectRates.fringe}%
            </div>
            <div className="h-full bg-fit-moderate-bg0 flex items-center justify-center text-white text-xs"
                 style={{ width: `${(wizardState.indirectRates.overhead / 100) * (100 / wrapRate)}%` }}>
              +{wizardState.indirectRates.overhead}%
            </div>
            <div className="h-full bg-orange-500 flex items-center justify-center text-white text-xs"
                 style={{ width: `${(wizardState.indirectRates.gAndA / 100) * (100 / wrapRate)}%` }}>
              +{wizardState.indirectRates.gAndA}%
            </div>
            <div className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs"
                 style={{ width: `${(wizardState.indirectRates.profit / 100) * (100 / wrapRate)}%` }}>
              +{wizardState.indirectRates.profit}%
            </div>
          </div>
        </div>

        {/* Rate Sliders */}
        <div className="grid grid-cols-2 gap-6">
          {Object.entries(INDUSTRY_BENCHMARKS).map(([key, benchmark]) => {
            const value = wizardState.indirectRates[key as keyof IndirectRates];
            const isInRange = value >= benchmark.min && value <= benchmark.max;

            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-bw-ink capitalize">
                    {key === 'gAndA' ? 'G&A' : key}
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={value}
                      onChange={(e) => updateIndirectRate(key as keyof IndirectRates, parseFloat(e.target.value) || 0)}
                      className="w-16 text-sm text-right border border-bw-border rounded px-2 py-1"
                    />
                    <Percent className="h-4 w-4 text-bw-muted ml-1" />
                  </div>
                </div>

                <input
                  type="range"
                  min={0}
                  max={key === 'overhead' ? 150 : 50}
                  value={value}
                  onChange={(e) => updateIndirectRate(key as keyof IndirectRates, parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />

                <div className="flex items-center justify-between text-xs">
                  <span className="text-bw-muted">{benchmark.description}</span>
                  <span className={isInRange ? 'text-success' : 'text-fit-moderate'}>
                    Industry: {benchmark.min}-{benchmark.max}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* AI Recommendation */}
        <div className="p-4 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
          <div className="flex items-start">
            <Sparkles className="h-5 w-5 text-bw-primary mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-bw-primary">AI Analysis</p>
              <p className="text-sm text-bw-primary mt-1">
                Your wrap rate of {wrapRate.toFixed(2)}x is {
                  wrapRate < 1.8 ? 'below' : wrapRate > 2.5 ? 'above' : 'within'
                } the typical federal contractor range (1.8x - 2.5x).
                {wrapRate < 1.8 && ' Consider if your rates fully capture all indirect costs.'}
                {wrapRate > 2.5 && ' This may make you less competitive on price-sensitive bids.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderODCStep = () => (
    <div className="space-y-6">
      {/* Common ODC Suggestions */}
      <div className="bg-bw-surface-subtle rounded-lg p-4 border border-purple-200">
        <div className="flex items-start">
          <Sparkles className="h-5 w-5 text-bw-primary mt-0.5 mr-2" />
          <div>
            <h4 className="font-medium text-bw-ink">Common ODC Categories</h4>
            <p className="text-sm text-bw-muted mt-1">
              Click to add common Other Direct Costs categories
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {['Travel', 'Equipment', 'Software Licenses', 'Training', 'Subcontractors', 'Materials', 'Facilities'].map((name) => {
            const isAdded = wizardState.odcCategories.some(
              cat => cat.name.toLowerCase() === name.toLowerCase()
            );
            return (
              <button
                key={name}
                onClick={() => !isAdded && setWizardState(prev => ({
                  ...prev,
                  odcCategories: [...prev.odcCategories, {
                    id: `new-${Date.now()}`,
                    name,
                    description: '',
                    estimatedAnnualCost: 0
                  }]
                }))}
                disabled={isAdded}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center transition-colors ${
                  isAdded
                    ? 'bg-success-bg text-success cursor-default'
                    : 'bg-bw-surface border border-purple-200 text-bw-primary hover:bg-bw-primary-tint'
                }`}
              >
                {isAdded ? <CheckCircle className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                {name}
              </button>
            );
          })}
        </div>
      </div>

      {/* ODC Categories List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-bw-ink">Your ODC Categories</h4>
          <button
            onClick={addODCCategory}
            className="flex items-center px-3 py-1.5 text-sm text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom
          </button>
        </div>

        {wizardState.odcCategories.length === 0 ? (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg">
            <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-bw-muted">No ODC categories yet</p>
            <p className="text-xs text-bw-muted mt-1">
              ODCs are optional but help with complete pricing
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {wizardState.odcCategories.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center gap-4 p-3 bg-bw-surface border border-bw-border rounded-lg"
              >
                <div className="flex-1">
                  <input
                    type="text"
                    value={cat.name}
                    onChange={(e) => updateODCCategory(cat.id, { name: e.target.value })}
                    placeholder="Category Name"
                    className="w-full text-sm font-medium border-none focus:ring-0 p-0"
                  />
                  <input
                    type="text"
                    value={cat.description}
                    onChange={(e) => updateODCCategory(cat.id, { description: e.target.value })}
                    placeholder="Description (optional)"
                    className="w-full text-xs text-bw-muted border-none focus:ring-0 p-0 mt-1"
                  />
                </div>
                <div className="w-40">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 text-bw-muted" />
                    <input
                      type="number"
                      value={cat.estimatedAnnualCost}
                      onChange={(e) => updateODCCategory(cat.id, { estimatedAnnualCost: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="w-28 text-sm border-none focus:ring-0 p-0"
                    />
                    <span className="text-xs text-bw-muted">/yr</span>
                  </div>
                </div>
                <button
                  onClick={() => removeODCCategory(cat.id)}
                  className="text-bw-muted hover:text-urgent transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const wrapRate = calculateWrapRate();
    const totalODC = wizardState.odcCategories.reduce((sum, cat) => sum + cat.estimatedAnnualCost, 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-bw-primary-tint rounded-lg p-4 border border-blue-200">
            <div className="flex items-center mb-2">
              <Users className="h-5 w-5 text-bw-primary mr-2" />
              <h4 className="font-medium text-bw-ink">Labor Categories</h4>
            </div>
            <p className="text-2xl font-bold text-bw-primary">{wizardState.laborCategories.length}</p>
            <p className="text-xs text-bw-muted mt-1">
              {wizardState.laborCategories.length >= 5 ? 'Good coverage' : 'Consider adding more'}
            </p>
          </div>

          <div className="bg-success-bg rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-2">
              <Calculator className="h-5 w-5 text-success mr-2" />
              <h4 className="font-medium text-bw-ink">Wrap Rate</h4>
            </div>
            <p className="text-2xl font-bold text-success">{wrapRate.toFixed(2)}x</p>
            <p className="text-xs text-bw-muted mt-1">
              {wrapRate >= 1.8 && wrapRate <= 2.5 ? 'Competitive range' : 'Review rates'}
            </p>
          </div>

          <div className="bg-bw-primary-tint rounded-lg p-4 border border-purple-200">
            <div className="flex items-center mb-2">
              <Package className="h-5 w-5 text-bw-primary mr-2" />
              <h4 className="font-medium text-bw-ink">ODC Categories</h4>
            </div>
            <p className="text-2xl font-bold text-bw-primary">{wizardState.odcCategories.length}</p>
            <p className="text-xs text-bw-muted mt-1">
              Est. {formatCurrency(totalODC)}/year
            </p>
          </div>
        </div>

        {/* Detailed Review */}
        <div className="space-y-4">
          {/* Labor Categories */}
          <div className="bg-bw-surface rounded-lg border border-bw-border p-4">
            <h4 className="font-medium text-bw-ink mb-3">Labor Categories</h4>
            {wizardState.laborCategories.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-bw-muted font-medium">Title</th>
                    <th className="text-right py-2 text-bw-muted font-medium">Base Rate</th>
                    <th className="text-right py-2 text-bw-muted font-medium">Loaded Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {wizardState.laborCategories.map((cat) => (
                    <tr key={cat.id} className="border-b last:border-b-0">
                      <td className="py-2">{cat.title}</td>
                      <td className="py-2 text-right">{formatCurrency(cat.baseRate)}/hr</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(cat.baseRate * wrapRate)}/hr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-bw-muted">No labor categories configured</p>
            )}
          </div>

          {/* Indirect Rates */}
          <div className="bg-bw-surface rounded-lg border border-bw-border p-4">
            <h4 className="font-medium text-bw-ink mb-3">Indirect Rates</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-bw-muted">Fringe</p>
                <p className="text-lg font-medium">{wizardState.indirectRates.fringe}%</p>
              </div>
              <div>
                <p className="text-xs text-bw-muted">Overhead</p>
                <p className="text-lg font-medium">{wizardState.indirectRates.overhead}%</p>
              </div>
              <div>
                <p className="text-xs text-bw-muted">G&A</p>
                <p className="text-lg font-medium">{wizardState.indirectRates.gAndA}%</p>
              </div>
              <div>
                <p className="text-xs text-bw-muted">Profit</p>
                <p className="text-lg font-medium">{wizardState.indirectRates.profit}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Validation */}
        {wizardState.laborCategories.length === 0 && (
          <div className="flex items-center p-3 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg">
            <AlertCircle className="h-5 w-5 text-fit-moderate mr-2" />
            <p className="text-sm text-fit-moderate">
              Consider adding labor categories for more complete proposals.
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Zap className="h-6 w-6 mr-2 text-bw-primary" />
            AI Pricing Wizard
          </h1>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isComplete = index < currentStep;

          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => setCurrentStep(index)}
                className={`flex flex-col items-center ${
                  isActive ? 'text-bw-primary' : isComplete ? 'text-success' : 'text-bw-muted'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  isActive ? 'bg-bw-primary text-white' :
                  isComplete ? 'bg-success-bg text-success' :
                  'bg-bw-surface-subtle text-bw-muted'
                }`}>
                  {isComplete ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <span className="text-sm font-medium">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  index < currentStep ? 'bg-green-300' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-6 mb-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-bw-ink">{STEPS[currentStep].title}</h2>
          <p className="text-sm text-bw-muted">{STEPS[currentStep].description}</p>
        </div>
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(prev => prev - 1)}
          disabled={currentStep === 0}
          className="flex items-center px-4 py-2 text-bw-muted hover:text-bw-ink disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </button>

        {currentStep < STEPS.length - 1 ? (
          <button
            onClick={() => setCurrentStep(prev => prev + 1)}
            className="flex items-center px-6 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-2 bg-success text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Pricing Profile
          </button>
        )}
      </div>
    </div>
  );
}
